import {
  getCommunityMembersApi,
  updateCommunityParticipantApi
} from '@/api/communities';
import ScrollArea from '@/components/DybrComponents/ScrollArea';
import {
  Block,
  Settings,
  SettingSectionTitle,
  SettingsFlexbox
} from '@/components/DybrComponents/StyledServicePagesElements';
import Checkbox from '@/components/FormElements/Checkbox';
import css from '@/scenes/Management/Community/CommunityMembers.module.css';
import ProfileLink from '@/scenes/Management/Community/ProfileLink';
import {
  useActiveProfile,
  useTokenState
} from '@/store/localStorage/useTokenState';
import { selectActiveProfileAugmented } from '@/store/user/selectors/selectActiveProfileAugmented';
import { classNames } from '@/utils/helpers/classNames';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

// Backend permission keys (see CommunityParticipant in @/api/schema.d.ts):
// "read" | "post" | "edit" | "impersonate" | "entry-admin" | "admin".
// There is no separate "comment" permission — "read" gates commenting.
const PERMISSIONS = {
  admin: 'admin',
  post: 'post',
  comment: 'read'
};

const STRINGS = {
  title: 'Участники сообщества',
  description:
    'Вы можете просмотреть список участников сообщества и управлять их правами доступа (администрирование, публикация записей и комментирование).',
  loading: 'Загрузка...',
  empty: 'В сообществе пока нет участников',
  loadError: 'Не удалось загрузить список участников',
  toggleError: nickname => `Не удалось изменить права для ${nickname}`,
  permissionLabels: {
    admin: 'админ',
    post: 'писать',
    comment: 'комментировать'
  }
};

export function CommunityMembers() {
  const { activeProfileId } = useActiveProfile();
  const profile = useSelector(state =>
    selectActiveProfileAugmented(state, { activeProfileId })
  );
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [token] = useTokenState();

  useEffect(() => {
    const loadMembers = async () => {
      setLoading(true);
      setError('');
      const res = await getCommunityMembersApi({
        token,
        communityId: profile.id
      });

      if (res.error) {
        setError(res.error || STRINGS.loadError);
      } else if (res.data) {
        setMembers(res.data ?? []);
      }
      setLoading(false);
    };

    if (profile.id) {
      loadMembers();
    }
  }, [profile.id, token]);

  const handleTogglePermission = async (member, permission) => {
    const currentPermissions = member.permissions || [];
    const hasPermission = currentPermissions.includes(permission);

    const newPermissions = hasPermission
      ? currentPermissions.filter(p => p !== permission)
      : [...currentPermissions, permission];

    const res = await updateCommunityParticipantApi({
      participantId: member.id,
      permissions: newPermissions,
      token
    });

    if (res.status === 'ok' || res.data) {
      setMembers(prev =>
        prev.map(m =>
          m.id === member.id ? { ...m, permissions: newPermissions } : m
        )
      );
    } else {
      alert(STRINGS.toggleError(member.profile?.nickname));
    }
  };

  return (
    <Block>
      <SettingsFlexbox>
        <SettingSectionTitle>
          <h4>{STRINGS.title}</h4>
          <p>{STRINGS.description}</p>
        </SettingSectionTitle>
        <Settings>
          {loading ? (
            <div className={css.message}>{STRINGS.loading}</div>
          ) : error ? (
            <div
              className={classNames({
                [css.message]: true,
                [css.error]: true
              })}
            >
              {error}
            </div>
          ) : members.length > 0 ? (
            <ScrollArea>
              <div className={css.membersList}>
                {members.map(member => {
                  const currentPermissions = member.permissions || [];
                  const isAdmin = currentPermissions.includes(
                    PERMISSIONS.admin
                  );
                  const isWrite = currentPermissions.includes(PERMISSIONS.post);
                  const isComment = currentPermissions.includes(
                    PERMISSIONS.comment
                  );

                  return (
                    <div key={member.id} className={css.memberItem}>
                      <ProfileLink
                        profile={member.profile}
                        className={css.profileName}
                      />
                      <div className={css.actions}>
                        <Checkbox
                          className={css.permissionCheckbox}
                          label={STRINGS.permissionLabels.admin}
                          id={`${member.id}-${PERMISSIONS.admin}`}
                          checked={isAdmin}
                          onChange={() =>
                            handleTogglePermission(member, PERMISSIONS.admin)
                          }
                        />
                        <Checkbox
                          className={css.permissionCheckbox}
                          label={STRINGS.permissionLabels.post}
                          id={`${member.id}-${PERMISSIONS.post}`}
                          checked={isAdmin || isWrite}
                          disabled={isAdmin}
                          onChange={() =>
                            handleTogglePermission(member, PERMISSIONS.post)
                          }
                        />
                        <Checkbox
                          className={css.permissionCheckbox}
                          label={STRINGS.permissionLabels.comment}
                          id={`${member.id}-${PERMISSIONS.comment}`}
                          checked={isAdmin || isComment}
                          disabled={isAdmin}
                          onChange={() =>
                            handleTogglePermission(member, PERMISSIONS.comment)
                          }
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          ) : (
            <div className={css.message}>{STRINGS.empty}</div>
          )}
        </Settings>
      </SettingsFlexbox>
    </Block>
  );
}
